<template>
    <div>
        <v-card class="dispatches" outlined>
            <v-card-title class="page-heading primary">
                <v-row align="center">
                    <v-col cols="12" sm="9">{{ $t('dispatches.heading') }}</v-col>
                    <v-col cols="12" sm="3" class="d-flex justify-end">
                        <v-btn
                            v-if="!inventoryAccountingEnabled"
                            class="minw100" 
                            color="white" 
                            outlined 
                            @click="openDispatchForm()"
                        >
                            <v-icon left>fal fa-plus</v-icon>

                            {{ $t('general.add') }}
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-title>

            <v-card-title v-if="inventoryAccountingEnabled">
                <v-row>
                    <v-col>
                        <div class="pt-1">
                            <v-alert
                                icon="fal fa-exclamation-circle"
                                color="primary"
                                prominent
                                text
                            >
                                {{ $t('general.delivery_notes_button_disabled') }}
                            </v-alert>
                        </div>
                    </v-col>
                </v-row>
            </v-card-title>

            <v-card-text>
                <div class="buttons-header">
                    <v-text-field
                        v-model="filter"
                        :label="$t('general.search')"
                        append-icon="far fa-search"
                        clearable
                        clear-icon="fal fa-times"
                        single-line
                        hide-details
                        class="search"
                        @input="filterDispatches()"
                    />
                </div>

                <v-data-table
                    :headers="headers"
                    :items="dispatches"
                    :options.sync="pagination"
                    :footer-props="{ 'items-per-page-options': [10, 20, 50, 100] }"
                    :items-per-page.sync="pagination.itemsPerPage"
                    :server-items-length="pagination.total"
                    :loading="loading"
                    :mobile-breakpoint="0"
                    class="operations striped"
                >
                    <template slot="item" slot-scope="props">
                        <tr>
                            <td>{{ props.item.number }}</td>
                            <td>{{ props.item.date | date }}</td>
                            <td>{{ props.item.client.name }}</td>
                            <td>{{ props.item.storekeeper }}</td>
                            <td class="minw200">{{ props.item.comment }}</td>
                            <td align="center" class="w35 maxw60">
                                <v-menu bottom transition="slide-y-transition">
                                    <template v-slot:activator="{ on }">
                                        <v-btn v-on="on" icon>
                                            <v-icon>far fa-ellipsis-h</v-icon>
                                        </v-btn>
                                    </template>

                                    <v-list>
                                        <v-list-item
                                            key="view"
                                            @click="viewDispatchDetails(props.item)"
                                        >
                                            <v-list-item-action class="mr-4 flex justify-content-center">
                                                <v-icon>fal fa-eye</v-icon>
                                            </v-list-item-action>
                                            <v-list-item-title>{{ $t('general.view_details') }}</v-list-item-title>
                                        </v-list-item>

                                        <v-divider />

                                        <v-list-item
                                            key="print"
                                            @click="print(props.item)"
                                        >
                                            <v-list-item-action class="mr-4 flex justify-content-center">
                                                <v-icon>fal fa-print</v-icon>
                                            </v-list-item-action>
                                            <v-list-item-title>{{ $t('general.print') }}</v-list-item-title>
                                        </v-list-item>

                                        <v-divider />

                                        <v-list-item
                                            key="edit"
                                            :disabled="inventoryAccountingEnabled"
                                            @click="openDispatchForm('edit', props.item)"
                                        >
                                            <v-list-item-action class="mr-4 flex justify-content-center">
                                                <v-icon>fal fa-pen</v-icon>
                                            </v-list-item-action>
                                            <v-list-item-title>{{ $t('general.edit') }}</v-list-item-title>
                                        </v-list-item>

                                        <v-divider />

                                        <v-list-item
                                            key="delete"
                                            :disabled="inventoryAccountingEnabled"
                                            @click="deleteDispatch(props.item)"
                                        >
                                            <v-list-item-action class="mr-4 flex justify-content-center">
                                                <v-icon :class="{ 'error--text': !inventoryAccountingEnabled }">fal fa-times</v-icon>
                                            </v-list-item-action>
                                            <v-list-item-title :class="{ 'error--text': !inventoryAccountingEnabled }">{{ $t('general.delete') }}</v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>

        <cod-dialog
            id="dispatchForm"
            :show.sync="formControl.visible"
            :drawer="formControl.drawer"
            max-width="700"
        >
            <!-- Header -->
            <template slot="header">
                <span v-if="formControl.mode == 'add'">{{ $t('dispatches.add_dispatch') }}</span>
                <span v-else>{{ $t('dispatches.edit_dispatch') }}</span>
                <v-spacer />
                <span class="mr-3" style="font-size: 14px">
                    {{ formControl.step === 1 ? $t('dispatches.dispatch_info') : $tc('general.item', 2) }}
                </span>
            </template>

            <!-- Content -->
            <div class="form-content" slot="content">
                <v-form
                    ref="dispatchForm"
                    v-model="formValid"
                    class="form-container"
                >
                    <v-window v-model="formControl.step" touchless style="height: 100%;">
                        <v-window-item :value="1">
                            <v-text-field
                                ref="numberField"
                                v-model="form.number"
                                class="mt-3"
                                :label="$t('general.number')"
                                :rules="form.rules.number"
                                :error-messages="form.errors.number"
                                validate-on-blur
                                autocomplete="off"
                                @input="
                                    $refs.numberField.validate();
                                    delete form.errors.number;
                                "
                            />

                            <v-menu
                                v-model="datePickerMenu"
                                :close-on-content-click="false"
                                offset-y
                                :nudge-right="33"
                                max-width="290px"
                                min-width="290px"
                                transition="scale-transition"
                                class="wfull"
                            >
                                <template v-slot:activator="{ on }">
                                    <v-text-field
                                        v-model="form.date"
                                        class="calendar-fix"
                                        :label="$t('general.date')"
                                        prepend-icon="fal fa-calendar"
                                        readonly
                                        v-on="on"
                                    />
                                </template>
                                <v-date-picker
                                    v-model="form.date"
                                    :first-day-of-week="1"
                                    no-title
                                    @input="datePickerMenu = false"
                                />
                            </v-menu>

                            <div class="flex">
                                <v-autocomplete
                                    ref="client_id"
                                    v-model="form.client_id"
                                    :items="clients"
                                    item-text="name"
                                    item-value="id"
                                    :label="$tc('general.buyer', 1)"
                                    :rules="form.rules.client_id"
                                    :error-messages="form.errors.client_id"
                                    menu-props="offsetY"
                                    auto-select-first
                                    validate-on-blur
                                    class="wfull mr-3"
                                />
                                <v-spacer />
                                <v-btn
                                    text
                                    color="primary"
                                    class="minw100 mt-3"
                                    @click="formControl.drawer = 'clientForm'"
                                >
                                    <v-icon left>fal fa-plus</v-icon>
                                    {{ $t('general.add') }}
                                </v-btn>
                            </div>

                            <v-text-field
                                v-model="form.storekeeper"
                                :label="$t('dispatches.dispatched_by')"
                                :rules="form.rules.storekeeper"
                                :error-messages="form.errors.storekeeper"
                                validate-on-blur
                            />

                            <v-textarea
                                v-model="form.comment"
                                hide-details
                                :rows="3"
                                :label="$t('general.comment')"
                            />
                        </v-window-item>

                        <v-window-item :value="2">
                            <search-product
                                ref="searchProduct"
                                class="my-5"
                                hide-details
                                :preserve-selection="false"
                                :products="products"
                                @select="selectProduct($event.product, $event.quantity)"
                            />

                            <div class="add-product">
                                <number-field
                                    v-model="itemQuantity"
                                    :label="$t('general.quantity')"
                                    :precision="3"
                                    :disabled="!productToAdd"
                                    class="pr-1"
                                    style="max-width: 80px"
                                />

                                <number-field
                                    v-model="itemQuantityMultiplier"
                                    :label="$t('general.multiplied_by')"
                                    :precision="3"
                                    prefix="×"
                                    class="pl-1"
                                    :disabled="!productToAdd"
                                    style="max-width: 90px"
                                />

                                <div style="word-break: break-all; min-width: 95px">
                                    {{ totalQuantity }}
                                </div>

                                <div class="text-body-1 ml-2">
                                    {{ productToAdd ? productToAdd.name : '' }}
                                </div>

                                <v-spacer />

                                <v-btn
                                    class="minw100 mb-1"
                                    color="primary"
                                    text
                                    :disabled="!productToAdd || !itemQuantity || !itemQuantityMultiplier"
                                    @click="addItem()"
                                >
                                    {{ $t('general.add') }}
                                </v-btn>
                            </div>

                            <v-slide-y-transition>
                                <div v-if="items.length">
                                    <v-data-table
                                        class="dispatch-items-table"
                                        :items="items"
                                        :items-per-page="5"
                                        :mobile-breakpoint="0"
                                        :headers="productHeaders"
                                        :footer-props="{ 'items-per-page-options': [5, 10, 20, 50] }"
                                    >
                                        <template slot="item" slot-scope="props">
                                            <tr>
                                                <td width="120px">
                                                    {{ parseFloat(props.item.count) + ' ' + getUnitOfMeasure(props.item.product) }}
                                                </td>
                                                <td>{{ props.item.product.name }}</td>
                                                <td>{{ props.item.product.code }}</td>
                                                <td align="right">
                                                    <v-tooltip top>
                                                        <template v-slot:activator="{ on }">
                                                            <span
                                                                class="cursor-pointer px-1"
                                                                v-on="on"
                                                                @click.stop="deleteItem(props.item)"
                                                            >
                                                                <i
                                                                    class="fal fa-times error--text"
                                                                    style="font-size: 20px;"
                                                                />
                                                            </span>
                                                        </template>
                                                        <span>{{ $t('general.delete') }}</span>
                                                    </v-tooltip>
                                                </td>
                                            </tr>
                                        </template>
                                    </v-data-table>
                                </div>
                            </v-slide-y-transition>
                        </v-window-item>
                    </v-window>
                </v-form>
            </div>

            <!-- Footer -->
            <template slot="footer">
                <v-btn
                    class="minw100 flex-1 mr-1"
                    color="primary"
                    outlined
                    :disabled="form.busy"
                    @click="previousStep()"
                >
                    {{ formControl.step === 1 ? $t('general.cancel') : $t('general.back') }}
                </v-btn>
                <v-btn
                    class="minw100 flex-1 ml-1"
                    color="primary"
                    depressed
                    :loading="form.busy"
                    :disabled="form.busy || (formControl.step === 2 && !items.length)"
                    @click="nextStep()"
                >
                    {{ formControl.step === 1 ? $t('general.next') : $t('general.save') }}
                </v-btn>
            </template>

            <!-- Drawer -->
            <div slot="drawer" class="drawer">
                <v-toolbar
                    class="drawer-header"
                    absolute
                    dark
                    flat
                    color="primary"
                >
                    <v-btn icon dark @click="formControl.drawer = null">
                        <v-icon>fal fa-times</v-icon>
                    </v-btn>

                    <v-toolbar-title>
                        <span>{{ $t('general.add_buyer') }}</span>
                    </v-toolbar-title>
                </v-toolbar>

                <client-form
                    class="drawer-content"
                    :admin="true"
                    :pricelists="pricelists"
                    @save="onClientSaved"
                />
            </div>
        </cod-dialog>

        <cod-dialog :show.sync="formControl.detailsFormVisible">
            <template slot="header">
                <span>{{ $t('general.dispatch') }}: {{ form.number }}</span>
            </template>

            <div slot="content" class="form-container">
                <v-simple-table>
                    <template v-slot:default>
                        <thead>
                            <tr>
                                <th class="text-left">{{ $tc('general.product', 1) }}</th>
                                <th class="text-left">{{ $tc('general.code') }}</th>
                                <th class="text-right">{{ $t('general.quantity') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="item in items" :key="item.id">
                                <td>{{ item.product.name }}</td>
                                <td>{{ item.product.code }}</td>
                                <td class="text-right">
                                    {{ _.round(item.count, 3) }} {{ getUnitOfMeasure(item.product) }}
                                </td>
                            </tr>
                        </tbody>
                    </template>
                </v-simple-table>
            </div>
        </cod-dialog>
    </div>
</template>

<script>
import { loadData } from '@/views/shared/loadPageData';
import CodDialog from '@/components/CodDialog';
import NumberField from '@/components/NumberField';
import ApiService from '@/services/ApiService';
import ClientForm from '@/views/shared/components/ClientForm';
import SearchProduct from '@/views/shared/components/SearchProduct';
import Product from '@/models/Product';
import Client from '@/models/Client';
import Dispatch from '@/models/Dispatch';
import HtmlGen from '@/wrappers/HtmlGen';

export default {
    components: {
        CodDialog,
        NumberField,
        ClientForm,
        SearchProduct
    },

    created() {
        loadData('/admin/load/dispatches').then(data => this.onDataLoaded(data));
    },

    data() {
        return {
            products: [],
            clients: [],
            dispatches: [],
            pricelists: [],
            filter: null,
            loading: false,

            headers: [
                { text: this.$t('general.number'), value: 'number' },
                { text: this.$t('general.date'), value: 'date' },
                { text: this.$tc('general.buyer', 1), value: 'name' },
                { text: this.$t('dispatches.dispatched_by'), value: 'storekeeper' },
                { text: this.$t('general.comment'), value: 'comment', sortable: false },
                { text: '', sortable: false }
            ],

            productHeaders: [
                { text: this.$t('general.quantity'), value: 'count' },
                { text: this.$tc('general.product'), value: 'product.name' },
                { text: this.$t('general.code'), value: 'product.code' },
                { text: '', sortable: false }
            ],

            pagination: {
                sortBy: ['date'],
                itemsPerPage: 20,
                page: 1,
                total: 0,
                sortDesc: [true]
            },

            form: this.createForm(),
            formValid: false,

            formControl: {
                mode: 'add',
                step: 1,
                visible: false,
                drawer: null,
                detailsFormVisible: false
            },

            datePickerMenu: false,

            items: [],
            productToAdd: null,
            itemQuantity: 1,
            itemQuantityMultiplier: 1,
        };
    },

    computed: {
        totalQuantity() {
            return this.productToAdd && this.itemQuantity && this.itemQuantityMultiplier
                ? `= ${_.round((this.itemQuantityMultiplier * this.itemQuantity), 3)} ${this.getUnitOfMeasure(this.productToAdd)}`
                : ''
        },

        inventoryAccountingEnabled() {
            return !!this.tenant.venue?.settings?.use_inventory_accounting;
        },
    },

    watch: {
        pagination: {
            handler() {
                this.getDispatches();
            },
            deep: true
        },

        formControl: {
            handler() {
                if (!this.formControl.visible) {
                    this.formControl.drawer = null;
                    this.productToAdd = null;
                }
            },
            deep: true
        }
    },

    methods: {
        // Place loaded data into adequate variables
        onDataLoaded(data) {
            this.products = data.products.map(p => new Product(p)).filter(p => {
                const isGoods = p.is_goods === null || !!p.is_goods;
                return isGoods && !p.usesInventoryAccounting();
            });
            this.clients = data.clients.map(c => new Client(c));
            this.pricelists = data.pricelists
        },

        // Get paginated, filtered and sorted dispatches from the server
        getDispatches() {
            const { sortBy, sortDesc, page, itemsPerPage } = this.pagination;
            const sortField = sortBy[0] ? sortBy[0] : 'date';
            const sortOrder = sortDesc[0] ? 'descending' : 'ascending';

            let url = `/admin/dispatches?page=${page}&itemsPerPage=${itemsPerPage}&sortField=${sortField}&sortOrder=${sortOrder}`;

            if (this.filter) {
                url += '&filter=' + this.filter;
            }

            this.loading = true;

            ApiService.get(url).then(response => {
                this.loading = false;
                this.dispatches = response.data.data.map(d => new Dispatch(d));
                this.pagination.total = response.data.total;
            });
        },

        // Filter dispatches
        filterDispatches: _.debounce(function() {
            this.getDispatches();
        }, 500),

        // Create dispatch form
        createForm(dispatch = null) {
            return new CodForm({
                number: dispatch ? dispatch.number : null,
                date: dispatch ? moment(dispatch.date).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
                client_id: dispatch ? dispatch.client_id : null,
                storekeeper: dispatch ? dispatch.storekeeper : null,
                comment: dispatch ? dispatch.comment : null,
                id: dispatch ? dispatch.id : null,

                rules: {
                    number: [CodForm.rule('required')],
                    date: [CodForm.rule('required')],
                    client_id: [CodForm.rule('required')],
                    storekeeper: [CodForm.rule('required')],
                }
            });
        },

        // Open form to add or edit a dispatch
        openDispatchForm(mode = 'add', dispatch = null) {
            this.form = this.createForm(dispatch);
            this.items = dispatch ? _.cloneDeep(dispatch.items) : [];

            this.formControl.mode = mode;
            this.formControl.step = 1;
            this.formControl.visible = true;

            this.$nextTick(() => {
                this.$nextTick(() => {
                    if(this.$refs.dispatchForm) this.$refs.dispatchForm.resetValidation();

                    if (mode !== 'edit') {
                        this.$refs.numberField.focus();
                    }
                });
            });
        },

        // Go to previous step in the dispatch form
        previousStep() {
            if (this.formControl.step !== 1) {
                this.formControl.step -= 1;
            } else {
                this.formControl.visible = false;
            }
        },

        // Go to next step in the dispatch form
        nextStep() {
            if (this.formControl.step !== 2) {
                if (!this.form.number || !this.form.client_id || !this.form.storekeeper) {
                    if(this.$refs.dispatchForm) this.$refs.dispatchForm.resetValidation();

                    setTimeout(() => {
                        this.$refs.dispatchForm.validate();
                    }, 200);

                    return false;
                }

                if(this.$refs.dispatchForm) this.$refs.dispatchForm.resetValidation();

                this.formControl.step += 1;
            } else {
                if (!this.items.length) {
                    if(this.$refs.dispatchForm) this.$refs.dispatchForm.resetValidation();

                    setTimeout(() => {
                        this.$refs.dispatchForm.validate();
                    }, 200);

                    return false;
                }
                this.saveDispatch();
            }
        },

        selectProduct(product, quantity) {
            this.productToAdd = product
            this.itemQuantity = quantity || 1
        },

        // Add item to the dispatch
        addItem() {
            const addedItem = this.items.find(item => item.product_id === this.productToAdd.id)
            if(addedItem) {
                addedItem.count = (parseFloat(addedItem.count) + parseFloat(this.itemQuantityMultiplier * this.itemQuantity))
                    .toFixed(3)
                this.items = [addedItem, ...this.items.filter(i => i.product_id !== this.productToAdd.id)]
            } else {
                this.items.unshift({
                    product: this.productToAdd,
                    product_id: this.productToAdd.id,
                    count: parseFloat((this.itemQuantityMultiplier * this.itemQuantity).toFixed(3))
                });
            }

            this.$refs.dispatchForm.validate()
            this.productToAdd = null;
            this.itemQuantity = 1;
            this.itemQuantityMultiplier = 1;
        },

        // View dispatched products
        viewDispatchDetails(dispatch) {
            this.form = this.createForm(dispatch);
            this.items = dispatch ? _.cloneDeep(dispatch.items) : [];
            this.formControl.detailsFormVisible = true;
        },

        // Delete item from the dispatch
        deleteItem(dispatchItem) {
            this.items = this.items.filter(item => item.product.id !== dispatchItem.product.id);
            if (!this.items.length) {
                this.form.is_made = false;
            }
        },

        // Save dispatch
        saveDispatch() {
            const formValid = this.$refs.dispatchForm.validate();
            if (!formValid) return false;

            this.form.items = this.items.map(item => {
                return { product_id: item.product_id, count: item.count };
            });

            if (this.formControl.mode !== 'edit') {
                ApiService.post('/admin/dispatches', this.form)
                    .then(response => {
                        this.formControl.visible = false;
                        this.dispatches.unshift(new Dispatch(response.data));
                        this.pagination.total++;
                        this.$snackbar(this.$t('dispatches.saved'), 'success');
                    })
                    .catch(() => {
                        if (
                            this.form.errors.number ||
                            this.form.errors.client_id ||
                            this.form.errors.storekeeper
                        ) {
                            this.formControl.step = 1;
                        }
                    });
            } else {
                ApiService.patch(`/admin/dispatches/${this.form.id}`, this.form)
                    .then(response => {
                        this.formControl.visible = false;
                        this.dispatches.forEach((item, index, array) => {
                            if (item.id === this.form.id) {
                                this.$set(array, index, new Dispatch(response.data));
                            }
                        });
                        this.$snackbar(this.$t('dispatches.updated'), 'success');
                    })
                    .catch(() => {
                        if (
                            this.form.errors.number ||
                            this.form.errors.client_id ||
                            this.form.errors.storekeeper
                        ) {
                            this.formControl.step = 1;
                        }
                    });
            }
        },

        // Delete dispatch
        deleteDispatch(dispatch) {
            this.$confirm(
                this.$t('dispatches.permanently_delete'),
                {
                    buttonTrueText: this.$t('general.confirm'),
                    icon: 'fal fa-times',
                    color: 'error',
                    title: this.$t('general.delete')
                },
                result => {
                    if (!result) return;

                    this.formControl.visible = false;
                    ApiService.delete(`/admin/dispatches/${dispatch.id}`, {}).then(response => {
                        this.dispatches = this.dispatches.filter(item => item.id !== dispatch.id);
                        this.pagination.total--;
                        this.$snackbar(this.$t('dispatches.deleted'), 'success');
                    });
                }
            );
        },

        print(item) {
            const html = item.toTextLines().map(line => HtmlGen.fromTextLine(line)).join('');
            this.$printService.printCustom(null, html, null, { choosePrinter: true })
        },

        // Save new client to the server
        onClientSaved(client) {
            this.formControl.drawer = null;
            this.clients.push(client.$toJson());
            this.form.client_id = client.id;
            this.$snackbar(this.$t('general.client_saved'), 'success');
            this.$nextTick(() => {
                this.$refs.client_id.validate();
            });
        },

        /**
         * Try to translate unit of measure
         */
        getUnitOfMeasure(product) {
            const unit = product.unit_of_measure_sold || product.unit_of_measure
            return this.$t(`products.unit_of_measure.${unit}`) !== `products.unit_of_measure.${unit}`
                ? this.$t(`products.unit_of_measure.${unit}`)
                : unit
        }
    }
};
</script>

<style lang="scss" scoped>
.dispatches {
    .page-heading {
        padding: 12px 16px;
        color: white;
        font-size: 16px;
        word-break: break-word;
    }

    .buttons-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;

        @media screen and (max-width: 599px) {
            flex-wrap: wrap;
            justify-content: flex-end;
        }

        .search {
            margin-bottom: 10px;
            padding-bottom: 5px;
            @media screen and (min-width: 600px) {
                max-width: 450px;
            }
        }
    }

}

.calendar-fix {
    ::v-deep .v-label--active {
        padding-right: 5px;
    }
}

.dispatch-items-table {
    overflow-y: auto;
    max-height: 350px;
}

.drawer {
    .drawer-header {
        font-weight: 500;
        height: 64px;
        width: 100%;
    }

    .drawer-content {
        margin-top: 60px;
    }
}

.form-content {
    padding: 4px 20px;
    height: 100%;

    .form-container {
        height: 520px;

        .add-product {
            display: flex;
            align-items: center;
            padding: 8px 12px;
            background: #eff2f7;
            border-radius: 3px;
            margin-bottom: 8px;
        }
    }
}
</style>
